import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Clock, Users, Plus, ChevronRight, X, Edit, Trash2 } from 'lucide-react';
import { format, parseISO, isAfter, isBefore, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import BookingForm from '@/components/BookingForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RoomDisplay() {
  const { roomId } = useParams();
  const [room, setRoom] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentBooking, setCurrentBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  
  // Welcome image carousel
  const [welcomeImages, setWelcomeImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Meeting info carousel (for top bar)
  const [currentMeetingIndex, setCurrentMeetingIndex] = useState(0);

  useEffect(() => {
    fetchData();
    const dataInterval = setInterval(fetchData, 60000);
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    
    return () => {
      clearInterval(dataInterval);
      clearInterval(timeInterval);
    };
  }, [roomId]);

  // Rotate welcome images every 8 seconds
  useEffect(() => {
    if (welcomeImages.length > 1) {
      const imageInterval = setInterval(() => {
        setCurrentImageIndex(prev => (prev + 1) % welcomeImages.length);
      }, 8000);
      return () => clearInterval(imageInterval);
    }
  }, [welcomeImages.length]);

  // Rotate meeting info every 5 seconds
  useEffect(() => {
    if (bookings.length > 1) {
      const meetingInterval = setInterval(() => {
        setCurrentMeetingIndex(prev => (prev + 1) % bookings.length);
      }, 5000);
      return () => clearInterval(meetingInterval);
    }
  }, [bookings.length]);

  useEffect(() => {
    if (bookings.length > 0) {
      updateCurrentBooking();
      updateWelcomeImages();
    } else {
      setWelcomeImages([]);
      setCurrentBooking(null);
    }
  }, [bookings, currentTime]);

  const fetchData = async () => {
    try {
      const [roomsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/public/rooms`),
        axios.get(`${API}/public/bookings`)
      ]);
      
      const foundRoom = roomsRes.data.find(r => r.id === roomId);
      setRoom(foundRoom);
      
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayBookings = bookingsRes.data
        .filter(b => b.room_id === roomId && b.start_time.startsWith(today))
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      
      setBookings(todayBookings);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateWelcomeImages = () => {
    const now = new Date();
    const imagesData = bookings
      .filter(b => (b.welcome_image || b.visitor_name || b.display_message) && isAfter(parseISO(b.end_time), now))
      .map(b => ({
        image: b.welcome_image ? (b.welcome_image.startsWith('http') ? b.welcome_image : `${BACKEND_URL}${b.welcome_image}`) : null,
        visitorName: b.visitor_name || b.responsible,
        displayMessage: b.display_message || '',
        meetingName: b.meeting_name,
        startTime: b.start_time,
        endTime: b.end_time,
        bookingId: b.id
      }));
    
    setWelcomeImages(imagesData);
    if (currentImageIndex >= imagesData.length) {
      setCurrentImageIndex(0);
    }
  };

  const handleCancelBooking = async (bookingId, hasRecurrence) => {
    let deleteSeries = false;
    
    if (hasRecurrence) {
      const choice = window.confirm(
        'Esta reunião faz parte de uma série recorrente.\n\n' +
        'Clique em OK para cancelar TODAS as reuniões da série\n' +
        'Clique em Cancelar para cancelar APENAS esta reunião'
      );
      deleteSeries = choice;
    } else {
      if (!window.confirm('Tem certeza que deseja cancelar esta reunião?')) {
        return;
      }
    }

    try {
      await axios.delete(`${API}/public/bookings/${bookingId}?delete_series=${deleteSeries}`);
      fetchData();
    } catch (error) {
      console.error('Erro ao cancelar reunião:', error);
      alert('Erro ao cancelar reunião');
    }
  };

  const handleEditBooking = (booking) => {
    setEditingBooking(booking);
    setShowBookingForm(true);
  };

  const updateCurrentBooking = () => {
    const now = new Date();
    
    for (const booking of bookings) {
      const start = parseISO(booking.start_time);
      const end = parseISO(booking.end_time);
      
      if (isWithinInterval(now, { start, end })) {
        setCurrentBooking(booking);
        return;
      }
    }
    setCurrentBooking(null);
  };

  const formatTime = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'HH:mm', { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const getNextMeetings = () => {
    const now = new Date();
    return bookings.filter(b => isAfter(parseISO(b.start_time), now) || 
      isWithinInterval(now, { start: parseISO(b.start_time), end: parseISO(b.end_time) }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white"></div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Sala não encontrada</h1>
          <p className="text-gray-400">ID: {roomId}</p>
        </div>
      </div>
    );
  }

  const isRoomOccupied = !!currentBooking;
  const hasWelcomeContent = welcomeImages.length > 0;
  const currentWelcome = welcomeImages[currentImageIndex];
  const hasWelcomeImage = Boolean(currentWelcome?.image);
  const nextMeetings = getNextMeetings();
  const displayMeeting = nextMeetings[currentMeetingIndex % nextMeetings.length];
  const allMeetingsEnded = bookings.length === 0 || nextMeetings.length === 0;

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Botão Flutuante para Nova Reunião */}
      <button
        onClick={() => setShowBookingForm(true)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-white/20 hover:bg-white/30 backdrop-blur-md text-white rounded-full shadow-2xl transition-all hover:scale-110 z-50 flex items-center justify-center border border-white/30"
        data-testid="add-booking-touchscreen-button"
      >
        <Plus className="w-8 h-8" />
      </button>

      {/* ===== IMAGEM DE FUNDO (ÁREA PRINCIPAL) ===== */}
      {hasWelcomeImage && currentWelcome ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src={currentWelcome.image}
            alt="Boas-vindas"
            className="max-w-full max-h-full object-contain"
            style={{ maxHeight: '100vh', maxWidth: '100vw' }}
          />
        </div>
      ) : room?.idle_video && allMeetingsEnded ? (
        // Vídeo de sala ociosa quando não há reuniões no dia
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <video
            src={room.idle_video.startsWith('http') ? room.idle_video : `${BACKEND_URL}${room.idle_video}`}
            className="max-w-full max-h-full object-contain"
            style={{ maxHeight: '100vh', maxWidth: '100vw' }}
            autoPlay
            loop
            muted
            playsInline
          />
        </div>
      ) : (
        // Fundo padrão quando não há imagem nem vídeo
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white/30">
              <div className="text-[120px] font-bold tracking-tight leading-tight">
                {room.name}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== BARRA SUPERIOR - STATUS E PRÓXIMAS REUNIÕES ===== */}
      <div className="absolute top-0 left-0 right-0 z-10">
        <div className="bg-gradient-to-b from-black/80 via-black/60 to-transparent p-6 pb-16">
          <div className="flex items-start justify-between">
            {/* Lado Esquerdo - Sala e Status */}
            <div className="flex items-center gap-6">
              {/* Status Indicator */}
              <div className={`px-6 py-3 rounded-full font-bold text-xl flex items-center gap-3 ${
                isRoomOccupied 
                  ? 'bg-red-500 text-white' 
                  : 'bg-green-500 text-white'
              }`}>
                <div className={`w-4 h-4 rounded-full animate-pulse ${
                  isRoomOccupied ? 'bg-white' : 'bg-white'
                }`} />
                {isRoomOccupied ? 'OCUPADA' : 'LIVRE'}
              </div>
              
              {/* Room Name */}
              <div className="text-white">
                <h1 className="text-3xl font-bold">{room.name}</h1>
                <p className="text-white/60 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {room.capacity} pessoas
                </p>
              </div>
            </div>

            {/* Lado Direito - Relógio */}
            <div className="text-right text-white">
              <div className="text-6xl font-mono font-bold tracking-tight">
                {format(currentTime, 'HH:mm')}
              </div>
              <div className="text-xl text-white/60 capitalize">
                {format(currentTime, "EEEE, dd 'de' MMMM", { locale: ptBR })}
              </div>
            </div>
          </div>

          {/* Próximas Reuniões - Carousel */}
          {nextMeetings.length > 0 && displayMeeting && (
            <div className="mt-6 bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    currentBooking?.id === displayMeeting.id 
                      ? 'bg-red-500/80 text-white' 
                      : 'bg-white/20 text-white'
                  }`}>
                    {currentBooking?.id === displayMeeting.id ? 'AGORA' : 'PRÓXIMA'}
                  </div>
                  <div className="text-white">
                    <h3 className="text-xl font-semibold">{displayMeeting.meeting_name}</h3>
                    <p className="text-white/70">
                      {displayMeeting.responsible} • {formatTime(displayMeeting.start_time)} - {formatTime(displayMeeting.end_time)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Botões de Ação */}
                  <button
                    onClick={() => handleEditBooking(displayMeeting)}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                    data-testid="edit-booking-button"
                    title="Editar reunião"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleCancelBooking(displayMeeting.id, displayMeeting.recurrence_id)}
                    className="p-2 bg-red-500/80 hover:bg-red-500 rounded-full text-white transition-colors"
                    data-testid="cancel-booking-button"
                    title="Cancelar reunião"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                  
                  {nextMeetings.length > 1 && (
                    <div className="flex items-center gap-2 text-white/60 ml-2">
                      <span className="text-sm">{currentMeetingIndex + 1}/{nextMeetings.length}</span>
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== BARRA INFERIOR - BOAS-VINDAS ===== */}
      {hasWelcomeContent && currentWelcome && (
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="bg-gradient-to-t from-black/90 via-black/70 to-transparent p-8 pt-20">
            <div className="flex items-end justify-between">
              <div className="text-white">
                <p className="text-2xl text-white/70 mb-2">✨ Bem-vindo(a)</p>
                <h2 className="text-5xl font-bold mb-3">
                  {currentWelcome.visitorName}
                </h2>
                {currentWelcome.displayMessage && (
                  <p className="text-xl text-white/80 mb-3 max-w-4xl">
                    {currentWelcome.displayMessage}
                  </p>
                )}
                <p className="text-xl text-white/70">
                  {currentWelcome.meetingName} • {formatTime(currentWelcome.startTime)} - {formatTime(currentWelcome.endTime)}
                </p>
              </div>

              {/* Indicadores de imagens */}
              {welcomeImages.length > 1 && (
                <div className="flex gap-2">
                  {welcomeImages.map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-2 rounded-full transition-all ${
                        idx === currentImageIndex 
                          ? 'w-8 bg-white' 
                          : 'w-2 bg-white/40'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== SEM IMAGEM - MOSTRAR INFO DA SALA ===== */}
      {!hasWelcomeContent && (
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="bg-gradient-to-t from-black/80 via-black/50 to-transparent p-8 pt-20">
            <div className="text-white text-center">
              {allMeetingsEnded ? (
                <div>
                  <p className="text-3xl font-light text-white/70 mb-2">Sala disponível</p>
                  <p className="text-xl text-white/50">
                    {bookings.length === 0 ? 'Nenhuma reunião agendada para hoje' : 'Todas as reuniões de hoje foram encerradas'}
                  </p>
                </div>
              ) : !currentBooking ? (
                <div>
                  <p className="text-2xl text-white/70 mb-2">Próxima reunião às</p>
                  <p className="text-5xl font-bold">{formatTime(nextMeetings[0]?.start_time)}</p>
                </div>
              ) : (
                <div>
                  <p className="text-2xl text-white/70 mb-2">Em reunião até</p>
                  <p className="text-5xl font-bold">{formatTime(currentBooking.end_time)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== LOGO TECNOSERVICE ===== */}
      <div className="absolute bottom-4 right-4 z-10 text-white/40 text-sm">
        Tecnoservice
      </div>

      {/* Modal de Nova/Editar Reunião */}
      {showBookingForm && room && (
        <BookingForm
          room={room}
          onClose={() => {
            setShowBookingForm(false);
            setEditingBooking(null);
          }}
          onSuccess={() => {
            setShowBookingForm(false);
            setEditingBooking(null);
            fetchData();
          }}
          existingBookings={bookings}
          isPublic={true}
          editingBooking={editingBooking}
        />
      )}
    </div>
  );
}
